// Elemental affinities parsed from item `info` strings.
//
// The mustad.io items API only structures a *weapon's own* element
// (item.stats.element). Defensive/offensive elemental properties — Strengthen
// (attacker boost), Absorb / Half / Weak / Cancel (defender) — live only in
// the free-text `info`, in a consistent form, e.g.:
//   "... Effect: Absorb Fire; Half Ice."
//   "... Effect: Strengthen Fire, Lightning, Ice."
//   "... Effect: +1 MA; Absorb Dark."
//
// This module extracts those into structured sets so the damage pipeline
// (MOD 2 / MOD 5) can apply the ×5/4 strengthen, ×2 weak, ÷2 half, and
// absorb-negation steps.

export const ELEMENTS = ['Fire', 'Lightning', 'Ice', 'Wind', 'Water', 'Earth', 'Holy', 'Dark'];
const ELEMENT_SET = new Set(ELEMENTS);

const KEYWORDS = ['Strengthen', 'Absorb', 'Half', 'Weak', 'Cancel'];

// Parse one item's `info` into { strengthen, absorb, half, weak, cancel },
// each an array of element names. Only element-typed clauses are captured;
// "Cancel Poison" (a status) or "Immune Don't Move" are ignored.
export function parseItemElements(info) {
  const out = { strengthen: [], absorb: [], half: [], weak: [], cancel: [] };
  if (!info) return out;
  for (const kw of KEYWORDS) {
    // "<Keyword> Elem[, Elem...]" — the char class stops at ';' or '.' so we
    // don't run past the clause; we also stop at the first non-element token.
    const re = new RegExp(`${kw}:?\\s+([A-Za-z, ]+)`, 'g');
    let m;
    while ((m = re.exec(info))) {
      const key = kw.toLowerCase();
      for (const tok of m[1].split(/,\s*/)) {
        const e = tok.trim();
        if (ELEMENT_SET.has(e)) out[key].push(e);
        else break; // e.g. "Strengthen Fire; Chance to cast Fire" -> stop at "Chance"
      }
    }
  }
  return out;
}

// Aggregate a unit's elemental profile across all equipped slots. `equipped`
// is the { Mainhand, Offhand, Head, Armor, Accessory } map of normalized
// items (each carrying an `info` string). Returns sets for de-duped lookup.
export function unitElementProfile(equipped) {
  const acc = {
    strengthen: new Set(), absorb: new Set(), half: new Set(),
    weak: new Set(), cancel: new Set(),
  };
  for (const item of Object.values(equipped)) {
    const p = parseItemElements(item && item.info);
    for (const key of Object.keys(acc)) {
      for (const e of p[key]) acc[key].add(e);
    }
  }
  return acc;
}

// NOTE: a monster's innate elemental affinities come from the LIVE tips
// (toolTips.Class[<class>]), whose "Effect: Half X; Weak Y, Z" text is the same
// format as item info — so parseItemElements handles it directly, no bespoke
// parser needed. (An earlier version read an "Elements:" clause from a committed
// jobStats snapshot, but that was stale — it claimed Tiamat weak Dark, Hydra
// half Fire — so the snapshot was retired.)

// Like unitElementProfile, but records WHICH equipped item granted each
// affinity → { strengthen: { Fire: ['Flame Shield'], … }, absorb: {…}, … }.
// Used for the "where it comes from" hover on the unit card.
export function unitElementSources(equipped) {
  const acc = { strengthen: {}, absorb: {}, half: {}, weak: {}, cancel: {} };
  for (const item of Object.values(equipped)) {
    if (!item || !item.info) continue;
    const p = parseItemElements(item.info);
    for (const key of Object.keys(acc)) {
      for (const e of p[key]) (acc[key][e] || (acc[key][e] = [])).push(item.name || 'gear');
    }
  }
  return acc;
}
